import * as React from 'react'
import { useParams } from 'react-router-dom'

import {
  Box,
  Button,
  Icon,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'

import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import type { Option } from 'modules/core/components/Popover/Content'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { automationSteps } from 'modules/automations/components/Show/Step/constants'
import type { AutomationStepType } from 'modules/automations/components/Show/Step/types'
import { isWeeklyOrMonthlyTrigger } from 'modules/automations/lib/isWeeklyOrMonthlyTrigger'
import { Action } from 'modules/automations/store/models/Action'

import styles from './index.module.css'

const cx = classNames.bind(styles)

const icons: Record<string, React.ComponentProps<typeof Icon.Medium>['icon']> = {
  trigger: 'play',
  condition: 'filter',
  action: 'arrow-right',
}
interface Props {
  disabled: boolean
  action?: Action
  title: string
  trigger?: string
  stepType: AutomationStepType
  conflict?: boolean
}

export function ActionStepHeader({
  disabled = false,
  action,
  title,
  trigger,
  stepType,
  conflict,
}: Props) {
  const { navigation } = useNavigation()

  const { id } = useParams()
  const [tooltipOpen, toggleTooltip] = useToggle()
  const [popoverOpen, togglePopover] = useToggle()

  const actionIsBranch = (): boolean => {
    if (!action) return false

    return action.attributes.actionable_type === Action.Types.branch
  }

  const stepTranslation = () => {
    switch (stepType) {
      case automationSteps.condition:
        return i18n.t('automations.steps.condition')

      case automationSteps.action:
        return i18n.t('automations.steps.action')

      default:
        return i18n.t('automations.steps.trigger')
    }
  }

  const actionOptions = (): Option[] => {
    if (!action) return []

    return [
      {
        to: navigation.settings.automations.id.details.actionsActionIdDelete.fullPath({
          id: action.attributes.automations_automation_id,
          action_id: action.attributes.id,
        }),
        title: actionIsBranch()
          ? i18n.t('automations.actions.delete_condition.title')
          : i18n.t('automations.actions.delete.title'),
        type: 'wrong',
      },
    ]
  }

  const triggerOptions = () => {
    if (trigger && id && isWeeklyOrMonthlyTrigger(trigger)) {
      return [
        {
          to: navigation.settings.automations.id.details.editDate.fullPath({ id }),
          title: i18n.t('automations.automation.edit_date.title'),
        },
      ]
    }

    return []
  }

  const popoverOptions = () => {
    if (!!action && !disabled) {
      return actionOptions()
    } else if (stepType === automationSteps.trigger) {
      return triggerOptions()
    } else {
      return []
    }
  }

  const icon = icons[stepType]
  const isNotificationAction = [
    Action.Types.requestReview,
    Action.Types.sendNotification,
    Action.Types.sendCustomNotification,
  ].includes(action?.attributes.actionable_type as string)

  return (
    <div className={styles.header}>
      <div className={styles.left}>
        {icon && (
          <div className={cx('iconWrapper', stepType)}>
            <Icon.Medium icon={icon} color="grey000White" />
          </div>
        )}
        <div className={styles.info}>
          <div className={cx('title', stepType)}>
            <Text.Normal size="200" weight="semibold" color="grey1000">
              {stepTranslation()}
            </Text.Normal>
          </div>
          <Box flexDirection="row" flexWrap="wrap">
            <Box flexDirection="row" gap="s12" style={{ marginRight: '8px' }}>
              <Text.Normal weight="normal">{title}</Text.Normal>
              {conflict && (
                <Tooltip.Dialog
                  open={tooltipOpen}
                  toggle={toggleTooltip}
                  attachment="bottom center"
                  label={
                    isNotificationAction
                      ? i18n.t('automations.conflict_tag_tooltip_action')
                      : i18n.t('automations.conflict_tag_tooltip_branch')
                  }
                >
                  <Tag type="champagne"> {i18n.t('automations.conflict_tag')} </Tag>
                </Tooltip.Dialog>
              )}
            </Box>
          </Box>
        </div>
      </div>
      {!!popoverOptions().length && (
        <div className={styles.right}>
          <Popover
            attachment="top right"
            targetAttachment="bottom center"
            toggle={togglePopover}
            open={popoverOpen}
            withPreventDefault
          >
            <Button onClick={togglePopover(true)} type="secondary" iconName="ellipsis" />
            <PopoverContent onClose={togglePopover(false)} options={popoverOptions()} />
          </Popover>
        </div>
      )}
    </div>
  )
}
